import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import HeroGuia from './components/HeroGuia';
import BankrollSection from './components/BankrollSection';
import StrategiesSection from './components/StrategiesSection';
import PsychologySection from './components/PsychologySection';
import OddsSection from './components/OddsSection';
import GlossarySection from './components/GlossarySection';

const chapters = [
  { id: 'bankroll', label: 'Bankroll', icon: 'ri-funds-line' },
  { id: 'estrategias', label: 'Estrategias', icon: 'ri-brain-line' },
  { id: 'psicologia', label: 'Psicología', icon: 'ri-shield-check-line' },
  { id: 'cuotas', label: 'Cuotas', icon: 'ri-scales-3-line' },
  { id: 'glosario', label: 'Glosario', icon: 'ri-book-open-line' },
];

const BASE = import.meta.env.VITE_SITE_URL ?? 'https://example.com';

const SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  '@id': `${BASE}/guia#article`,
  headline: 'Guía del Apostador — Estrategias, Bankroll y Cuotas',
  description: 'Guía completa para apostadores: gestión de bankroll, estrategias de value betting, psicología del juego, tipos de cuotas y glosario de términos de apuestas deportivas.',
  url: `${BASE}/guia`,
  inLanguage: 'es-ES',
  author: { '@type': 'Organization', name: 'Apuestamente', url: BASE },
  publisher: { '@type': 'Organization', name: 'Apuestamente', url: BASE },
  dateModified: '2026-04-02',
  mainEntityOfPage: { '@type': 'WebPage', '@id': `${BASE}/guia` },
  about: [
    { '@type': 'Thing', name: 'Gestión de bankroll en apuestas deportivas' },
    { '@type': 'Thing', name: 'Estrategias de apuestas deportivas' },
    { '@type': 'Thing', name: 'Value betting' },
    { '@type': 'Thing', name: 'Cuotas deportivas' },
  ],
};

export default function GuiaPage() {
  useEffect(() => {
    document.title = 'Guía del Apostador | Estrategias y Bankroll | Apuestamente';
    const id = 'schema-guia';
    let el = document.getElementById(id) as HTMLScriptElement | null;
    if (!el) {
      el = document.createElement('script');
      el.id = id;
      el.type = 'application/ld+json';
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(SCHEMA);
    return () => { document.getElementById(id)?.remove(); };
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-[#080a0f]">
      <Navbar />

      {/* Hero */}
      <HeroGuia />

      {/* Sticky chapter nav */}
      <div className="sticky top-16 z-40 bg-[#0d0f14]/95 backdrop-blur-md border-b border-[#1e2028]">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="flex items-center gap-1 overflow-x-auto py-2 scrollbar-hide">
            {chapters.map((c) => (
              <button
                key={c.id}
                onClick={() => scrollTo(c.id)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-[#6b7280] hover:text-white hover:bg-white/5 transition-all whitespace-nowrap cursor-pointer flex-shrink-0"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${c.icon} text-xs`}></i>
                </div>
                {c.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Sections */}
      <main>
        <BankrollSection />
        <StrategiesSection />
        <PsychologySection />
        <OddsSection />
        <GlossarySection />
      </main>

      {/* Final CTA */}
      <section className="py-20 bg-[#0a0c11] border-t border-[#1e2028]">
        <div className="max-w-[1440px] mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#00e87a]/10 border border-[#00e87a]/20 mb-6">
            <i className="ri-rocket-line text-[#00e87a] text-xs"></i>
            <span className="text-[#00e87a] text-xs font-semibold uppercase tracking-widest">Empieza hoy</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Syne', sans-serif" }}>
            Pon en práctica lo aprendido
          </h2>
          <p className="text-[#a8b2c1] text-base max-w-xl mx-auto mb-8">
            Usa las herramientas de Apuestamente para registrar tus apuestas, comparar cuotas y seguir tu evolución como apostador.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <a href="/comparador" className="px-6 py-3 bg-[#00e87a] text-black text-sm font-bold rounded-md hover:bg-[#00d070] transition-colors whitespace-nowrap cursor-pointer">
              Comparar cuotas ahora
            </a>
            <a href="/perfil" className="px-6 py-3 border border-[#2a2d3a] text-[#a8b2c1] text-sm font-medium rounded-md hover:border-[#00e87a]/40 hover:text-white transition-colors whitespace-nowrap cursor-pointer">
              Registrar mis apuestas
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
